import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc } from '@if/core/date'
import { EFinancingProgram, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { normalizeNumber } from '@if/core/utils'
import { Button, Grid, Paper, Stack } from '@mui/material'
import { Breadcrumb, PageError } from '@src/components'
import { useAppDispatch, useAppSelector } from '@src/data/store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { creditRslaWorksheetSelectors } from '@src/data/store/CreditRslaWorksheet/credit-rsla-worksheet-store'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication/full-credit-application-effects'
import { EditCreditRslaWorksheetDto, EditCreditRslaWorksheetDtoSchema } from '@src/data/types/CreditRslaWorksheet'
import { reportErrorToConsole } from '@src/services/error-logger'
import { FieldError, FieldErrorsImpl, Merge, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { appActions, appSelectors } from '../../data/store/AppStore'
import creditRslaWorksheetEffects from '../../data/store/CreditRslaWorksheet/credit-rsla-worksheet-effects'
import { Constants, Vehicle } from '../../data/types'
import { useMaxPaymentAmountWithAgentLeeway, useMaxPaymentError } from '../ViewWorksheet/viewWorksheet-hooks'
import TransactionForm from './components/TransactionForm'
import VehicleForm from './components/VehicleForm'
import { useFirstPaymentDateOptions } from '../EditWorksheet/editWorksheet-hooks'
const EditCreditRslaWorksheetPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const creditApplication = useAppSelector(creditSelectors.getCurrent)
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
    reset,
  } = useForm<EditCreditRslaWorksheetDto>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: EditCreditRslaWorksheetDtoSchema.getDefault() as unknown as EditCreditRslaWorksheetDto,
    resolver: yupResolver(EditCreditRslaWorksheetDtoSchema),
  })
  const dispatch = useAppDispatch()
  const merchant = useAppSelector(creditSelectors.getMerchant)
  const dispatchEffect = useSideEffect()
  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${EFinancingProgram.CreditRsla}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('worksheet.financing') },
  ]
  const error = useAppSelector(appSelectors.getBusinessError)
  const worksheet = useAppSelector(creditRslaWorksheetSelectors.getCurrent)
  const paymentFrequency = watch('paymentFrequency')
  const deliveryOn = watch('deliveryOn')
  const paymentAmountForFrequency = watch('paymentAmountForFrequency')
  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    EPaymentPlan.RegularDailyInterests,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )
  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    paymentAmountForFrequency,
    maxPaymentAmount,
    dispatch,
    Constants.financingProgramConfigs[EFinancingProgram.CreditRsla].validateMaxPaymentAmountInWorksheet,
  )
  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  const onFormSubmitted = async (data: EditCreditRslaWorksheetDto) => {
    const expiry = new Date(creditApplication!.expiresOn)
    const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )

    if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }]))
      return
    } else if (compareAsc(deliveryOnUTC, expiry) === 1) {
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }]))
      return
    }

    data.planName = creditApplication?.applicant?.creditDecision?.planName || ''
    data.amountRequested = amountRequested
    data.status = EWorksheetStatus.Active
    data.vehicle.pricingCategory = 'average'
    const adjustedWholePrices = data.vehicle.pricing.adjustedWholePrices
    if (adjustedWholePrices.avg > 0) {
      data.vehicle.thirdPartyEvaluationValue = adjustedWholePrices.avg
    }
    await dispatchEffect(creditRslaWorksheetEffects.update(data)).then(() =>
      dispatchEffect(fullCreditApplicationEffects.getById(creditApplication!.id, EFinancingProgram.CreditRsla)).then(
        () =>
          navigate(`/Applications/${EFinancingProgram.CreditRsla}/${creditApplication!.id}/view`, { replace: true }),
      ),
    )
  }

  const isLoading = useAppSelector(creditRslaWorksheetSelectors.isSavingPersonalLoanWorksheet)

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: worksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  return (
    <div>
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <div style={{ height: '100%' }}>
          <Paper>
            <PageError errors={error} />
            <input type="hidden" value=" " {...register('id')} />
            <Grid
              sx={{ flexDirection: 'row' }}
              size={{
                xs: 6,
                md: 3.9,
              }}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={3}>
                <div style={{ marginTop: '1%' }}>
                  <Button variant="contained" type="submit" disabled={isLoading || maxPaymentForFrequencyError != null}>
                    {t('common.save')}
                  </Button>
                </div>
              </Stack>
            </Grid>
            <VehicleForm
              defaultVehicle={worksheet!.vehicle}
              merchant={merchant}
              control={control}
              getValues={getValues}
              setValue={setValue}
              register={register}
              watch={watch}
              errors={errors.vehicle as Merge<FieldError, FieldErrorsImpl<Vehicle>>}
              title={t('worksheet.vehicle')}
            />
            <TransactionForm
              control={control}
              currentCreditApplication={creditApplication!}
              reset={reset}
              register={register}
              watch={watch}
              worksheet={worksheet!}
              errors={errors as Merge<FieldError, FieldErrorsImpl<EditCreditRslaWorksheetDto>>}
              creditApplicationInterestRate={creditApplication!.finalCreditDecision.interestRate}
              minFirstPaymentDate={minFirstPaymentDate}
              maxFirstPaymentDate={maxFirstPaymentDate}
            />
          </Paper>
        </div>
      </form>
    </div>
  )
}

export default EditCreditRslaWorksheetPage
